import type { Breadcrumb, Event } from "@sentry/nextjs";
import { TOKEN_ROUTES, TOKEN_API_PATHS } from "@constants/token-routes";

const REDACTED = "redacted";

// The token stays in the URL for the life of the request, so every page-view
// event, fetch breadcrumb and http.client span carries it.
const TOKEN_PATH = new RegExp(
  `/(${[...TOKEN_ROUTES, ...TOKEN_API_PATHS].join("|")})/[^/?#]+`,
  "g",
);

const TOKEN_STATUS_PATH = /\/api\/[^/?#]+\/(v\d+\/)?status/g;

export function redactToken<T>(value: T): T {
  return typeof value === "string"
    ? (value
        .replace(TOKEN_PATH, `/$1/${REDACTED}`)
        .replace(TOKEN_STATUS_PATH, `/api/${REDACTED}/$1status`) as T)
    : value;
}

function redactData(data: Record<string, unknown> | undefined): void {
  if (!data) {
    return;
  }

  Object.keys(data).forEach((key) => {
    data[key] = redactToken(data[key]);
  });
}

export function redactBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.message) {
    breadcrumb.message = redactToken(breadcrumb.message);
  }

  redactData(breadcrumb.data);

  return breadcrumb;
}

export function redactEvent<T extends Event>(event: T): T {
  if (event.request?.url) {
    event.request.url = redactToken(event.request.url);
  }

  if (event.transaction) {
    event.transaction = redactToken(event.transaction);
  }

  redactData(event.contexts?.trace?.data);
  // The fetch span's name — serialized as `description` — is `${method} ${url}`,
  // so it carries the token even though `data` has been scrubbed.
  event.spans?.forEach((span) => {
    span.description = redactToken(span.description);
    redactData(span.data);
  });

  return event;
}
